// Skills v2 library — Your skills tab (2026-08-19). Mockup #s5 bottom half.
//
// Outcome-first table of everything active across the tenant's agents. A subtle
// "Developer view" link in the header reveals the raw power-user surface
// (owned by SkillsView via the onOpenDeveloper prop).

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../theme/colors";
import { MATERIALS } from "../../theme/materials";
import { skillsApi } from "../../api/client";
import { healthPill, impactLabel, agentCountLabel, sourceLabel, toneColor, importedSkillNotice } from "./skillLibFormat";
import ImportSkillModal from "./ImportSkillModal";
import { HOSTED_PUSH_ENABLED } from "./hostedPushFlag";

const BORDER = COLORS.border || "#e2e5eb";
const MUTED = COLORS.textMuted || "#6b7280";
const DIM = COLORS.textDim || "#9090a8";
const GREEN = COLORS.green || "#10b981";
const BLUE = COLORS.accent || "#3b82f6";

function unwrapArray(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
}

function Pill({ label, tone }) {
  const color = toneColor(tone, COLORS);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 700,
      padding: "2px 8px", borderRadius: 999, color,
      background: `${color}1F`, border: `1px solid ${color}66`,
    }}>{tone === "warn" ? "▲" : "●"} {label}</span>
  );
}

const TH = { textAlign: "left", fontSize: 10, letterSpacing: "0.05em", textTransform: "uppercase", color: DIM, fontWeight: 700, padding: "9px 15px", borderBottom: `1px solid ${BORDER}`, whiteSpace: "nowrap" };
const TD = { padding: "12px 15px", borderBottom: `1px solid ${BORDER}`, verticalAlign: "middle", color: MUTED, fontSize: 13 };

export default function YourSkillsTab({ onOpenDeveloper }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [exportingName, setExportingName] = useState(null);
  const [pushingName, setPushingName] = useState(null);
  const [notice, setNotice] = useState(null);
  // Hosted-provider skills (companion to Push): what already lives on Anthropic.
  const [hosted, setHosted] = useState(undefined); // undefined=loading, null=unavailable, []=none
  const [hostedNote, setHostedNote] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(unwrapArray(await skillsApi.yourSkills()));
    } catch {
      setError("Couldn't load your skills.");
    } finally {
      setLoading(false);
    }
    if (HOSTED_PUSH_ENABLED) {
      try {
        setHosted(unwrapArray(await skillsApi.listHosted()));
      } catch (e) {
        setHosted(null);
        setHostedNote(/412|key|configured/i.test((e && e.message) || "")
          ? "Add your Anthropic API key in Admin → Integrations to see skills hosted there."
          : "Hosted skills are unavailable right now.");
      }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleExport = useCallback(async (name) => {
    setExportingName(name);
    setNotice(null);
    try {
      const res = await skillsApi.exportSkill(name);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}.skill.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setNotice(`Couldn't export "${name}".`);
    } finally {
      setExportingName(null);
    }
  }, []);

  const handlePush = useCallback(async (name) => {
    setPushingName(name);
    setNotice(null);
    try {
      await skillsApi.pushSkillHosted(name, "anthropic");
      setNotice(`Pushed "${name}" to your Anthropic workspace.`);
    } catch (e) {
      const msg = (e && e.message) || "";
      setNotice(
        /412|key|configured/i.test(msg)
          ? `Add your Anthropic API key in Admin → Integrations to push "${name}".`
          : `Couldn't push "${name}" to Anthropic.`,
      );
    } finally {
      setPushingName(null);
    }
  }, []);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h2 style={{ fontSize: 16, margin: 0, color: COLORS.text }}>Your skills</h2>
          <p style={{ margin: "4px 0 0", color: DIM, fontSize: 13, maxWidth: "66ch" }}>
            Everything active across your agents, in one place. Outcome-first — the raw
            SKILL.md / version / export lives in the developer view.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            type="button"
            onClick={() => setShowImport(true)}
            style={{
              background: BLUE, border: 0, color: "#fff",
              fontSize: 12.5, fontWeight: 700, padding: "8px 13px", borderRadius: 8, cursor: "pointer",
            }}
          >↧ Import skill</button>
          {onOpenDeveloper && (
            <button
              type="button"
              onClick={onOpenDeveloper}
              style={{
                background: "transparent", border: `1px solid ${BORDER}`, color: MUTED,
                fontSize: 12, fontWeight: 600, padding: "7px 12px", borderRadius: 8, cursor: "pointer",
              }}
            >Developer view →</button>
          )}
        </div>
      </div>

      {notice && (
        <div style={{ marginBottom: 12, fontSize: 12.5, color: COLORS.red || "#ef4444" }}>{notice}</div>
      )}

      {showImport && (
        <ImportSkillModal
          onClose={() => setShowImport(false)}
          onImported={(row, fileName) => {
            setNotice(importedSkillNotice(row, fileName));
            load();
          }}
        />
      )}

      {loading ? (
        <div style={{ fontSize: 13, color: DIM }}>Loading your skills…</div>
      ) : error ? (
        <div style={{ fontSize: 13, color: COLORS.red || "#ef4444" }}>{error}</div>
      ) : rows.length === 0 ? (
        <div style={{
          ...MATERIALS.thickGlass(), borderRadius: 16, padding: 20, fontSize: 13, color: DIM,
        }}>
          Nothing active yet — add a skill from an agent's Overview, or author one.
        </div>
      ) : (
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 16, overflow: "hidden", background: COLORS.surface }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead>
                <tr>
                  <th style={TH}>Skill</th>
                  <th style={TH}>On agents</th>
                  <th style={TH}>Impact</th>
                  <th style={TH}>Health</th>
                  <th style={TH}>Source</th>
                  <th style={{ ...TH, textAlign: "right" }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const pill = healthPill(r.health);
                  const impact = impactLabel(r.impact_pct);
                  const measuring = impact.startsWith("◷");
                  return (
                    <tr key={r.base_name}>
                      <td style={{ ...TD, color: COLORS.text, fontWeight: 600 }}>{r.display_name || r.base_name}</td>
                      <td style={TD}>
                        {Array.isArray(r.agent_ids) && r.agent_ids.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/library/${r.agent_ids[0]}`)}
                            title="Open the agent"
                            style={{ background: "none", border: "none", padding: 0, font: "inherit", fontSize: 13, cursor: "pointer", color: COLORS.accentText || COLORS.accent, fontWeight: 600 }}
                          >{agentCountLabel(r.agent_count)} ↗</button>
                        ) : agentCountLabel(r.agent_count)}
                      </td>
                      <td style={{ ...TD, color: measuring ? DIM : GREEN, fontWeight: 700 }}>{impact}</td>
                      <td style={TD}><Pill label={pill.label} tone={pill.tone} /></td>
                      <td style={TD}>
                        <span style={{
                          fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                          color: r.source === "authored" ? (COLORS.accentText || COLORS.accent) : DIM,
                          background: COLORS.surfaceHover || "transparent", border: `1px solid ${BORDER}`,
                          textTransform: "uppercase", letterSpacing: "0.03em",
                        }}>{sourceLabel(r.source)}</span>
                      </td>
                      <td style={{ ...TD, textAlign: "right" }}>
                        <button
                          type="button"
                          onClick={() => handleExport(r.base_name)}
                          disabled={exportingName === r.base_name}
                          title="Export as a standard SKILL.md bundle"
                          style={{
                            font: "inherit", fontSize: 12, fontWeight: 600, color: MUTED,
                            background: "transparent", border: `1px solid ${BORDER}`,
                            padding: "5px 10px", borderRadius: 8,
                            cursor: exportingName === r.base_name ? "default" : "pointer",
                          }}
                        >{exportingName === r.base_name ? "…" : "↥ Export"}</button>
                        {HOSTED_PUSH_ENABLED && (
                        <button
                          type="button"
                          onClick={() => handlePush(r.base_name)}
                          disabled={pushingName === r.base_name}
                          title="Push to your Anthropic workspace (outbound; content can't be pulled back)"
                          style={{
                            font: "inherit", fontSize: 12, fontWeight: 600, color: MUTED,
                            background: "transparent", border: `1px solid ${BORDER}`,
                            padding: "5px 10px", borderRadius: 8, marginLeft: 6,
                            cursor: pushingName === r.base_name ? "default" : "pointer",
                          }}
                        >{pushingName === r.base_name ? "…" : "↗ Push"}</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* On hosted providers — companion to Push (GET /api/skills/hosted).
          DORMANT: gated off (see HOSTED_PUSH_ENABLED at top of file). */}
      {HOSTED_PUSH_ENABLED && (
      <div style={{ marginTop: 22 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: DIM, marginBottom: 10 }}>
          On hosted providers
        </div>
        {hosted === undefined ? (
          <div style={{ fontSize: 12.5, color: DIM }}>Checking hosted providers…</div>
        ) : hosted === null ? (
          <div style={{ fontSize: 12.5, color: DIM }}>{hostedNote}</div>
        ) : hosted.length === 0 ? (
          <div style={{ fontSize: 12.5, color: DIM }}>
            Nothing pushed to a hosted provider yet. Use <b style={{ color: COLORS.text }}>↗ Push</b> on a skill above to export it to your hosted workspace.
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {hosted.map((h, i) => (
              <span key={h.id || h.name || i} style={{
                display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600,
                padding: "6px 11px", borderRadius: 999, color: COLORS.text,
                background: COLORS.surfaceHover, border: `1px solid ${BORDER}`,
              }}>
                <span style={{ color: DIM }}>{h.provider || "anthropic"}</span>
                {h.display_name || h.name || h.id}
              </span>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
